import { createActionGroup, emptyProps, props } from '@ngrx/store';

import * as fromGenerated from '../../_generated';

export type AdminTeammatesSortColumn = 'name' | 'skills' | 'groups' | 'users';
export type AdminTeammatesSortDirection = 'asc' | 'desc';

export type AdminTeammatesAccessMode = 'invited-only' | 'org-wide';
/**
 * One axis the visual picker can set (SB-3519).
 *
 * Named `Visual`, not `Avatar`: SB-3519 KD-13 made `avatar` a *discriminator
 * value* meaning shape-plus-face specifically, so a type called `AvatarPart`
 * whose union contains `'icon'` would invert the epic's own vocabulary.
 *
 * `color` is deliberately in the same union as the two exclusive pairs, because
 * BOTH visuals read it — the icon renders in the dark instance of the teammate's
 * color (spec Story 1). The reducer is where that distinction lives: a `color`
 * pick must never flip `visualType`. See `setVisualPart` in the reducer.
 */
export type VisualPart = 'color' | 'shape' | 'eyes' | 'icon';
export type TeammateTranslationFieldKey =
  'name' | 'title' | 'welcomeMessage' | 'purposeMessage';
export type TeammateBuilderLocale = 'en' | 'fr';

/** A staged invitee on the Builder's General Access panel. */
export interface AdminTeammateInvitee {
  kind: 'user' | 'group';
  id: string;
  displayName: string;
  /** email (users) or "Groupe · N membres" (groups) — for the chip's second line. */
  secondary?: string | undefined;
}

/**
 * Identity of an invitee within one teammate's share set. `kind` is part of the
 * key because a user and a group may share an id.
 */
export const inviteeKey = (i: { kind: string; id: string }): string =>
  `${i.kind}:${i.id}`;

/** Create body shape (matches the generated CreateShareableTeammateDto). */
export interface CreateTeammateBody {
  defaultLocale: fromGenerated.CreateShareableTeammateDto.DefaultLocaleEnum;
  color: fromGenerated.CreateShareableTeammateDto.ColorEnum;
  // SB-3519 KD-1: the visual is a self-describing group. `visualType` is required
  // on create — the column has no default, so every write names the visual it
  // intends — and exactly ONE type's fields travel with it, never the other's:
  // `shape` + `eyes` under `avatar`, `icon` under `icon`. The API rejects a mix
  // (`MatchesTeammateVisualTypeConstraint`, admin-teammates.validators.ts).
  //
  // All three are optional HERE so one interface can express both shapes; the
  // pairing is enforced by `visualGroup()` at the single construction site
  // (admin-teammate-builder-modal.component.ts) and by the API.
  //
  // Optional WITHOUT `| undefined`, matching the generated DTO exactly. Under
  // `exactOptionalPropertyTypes` the two are different types: `| undefined`
  // would also permit an explicit `shape: undefined`, which the generated DTO
  // rejects. `visualGroup()` omits the key instead of assigning undefined, so
  // nothing needs the wider type.
  visualType: fromGenerated.CreateShareableTeammateDto.VisualTypeEnum;
  shape?: fromGenerated.CreateShareableTeammateDto.ShapeEnum;
  eyes?: fromGenerated.CreateShareableTeammateDto.EyesEnum;
  icon?: string;
  translations: fromGenerated.CreateTeammateTranslationDto[];
  attachedSkillIds: string[];
}

export const AdminTeammatesActions = createActionGroup({
  source: 'AdminTeammates',
  events: {
    'Load Teammates': emptyProps(),
    'Load Teammates Success': props<{
      teammates: fromGenerated.AdminTeammateView[];
    }>(),
    'Load Teammates Failure': props<{ error: string }>(),

    'Set Sort': props<{
      column: AdminTeammatesSortColumn | null;
      direction: AdminTeammatesSortDirection;
    }>(),
    'Set Filter': props<{ filter: string }>(),

    // Lazy per-row expand payload for the Skills / Groups / Users count cells.
    'Load Dropdown Payload': props<{ teammateId: string }>(),
    'Load Dropdown Payload Success': props<{
      teammateId: string;
      payload: fromGenerated.TeammateDropdownPayloadView;
    }>(),
    'Load Dropdown Payload Failure': props<{
      teammateId: string;
      error: string;
    }>(),

    // ─── Builder data loads (fired on Builder open) ──────────────────────────
    'Load All Users': emptyProps(),
    'Load All Users Success': props<{ users: fromGenerated.AdminUserView[] }>(),
    'Load All Users Failure': props<{ error: string }>(),
    'Load All Groups': emptyProps(),
    // The 'all' (org-wide) group id is derived from this list in the reducer.
    'Load All Groups Success': props<{
      groups: fromGenerated.GroupView[];
    }>(),
    'Load All Groups Failure': props<{ error: string }>(),

    // ─── Overlay lifecycle ───────────────────────────────────────────────────
    'Open Create Modal': emptyProps(),
    'Open Edit Modal': props<{ teammate: fromGenerated.AdminTeammateView }>(),
    // Seeds the edit Builder's staged invitees (chained off Open Edit Modal).
    'Seed Edit Invitees': props<{ invitees: AdminTeammateInvitee[] }>(),
    // Seeds the edit Builder's attached skills (from the dropdown payload).
    'Seed Edit Skills': props<{ skillIds: string[] }>(),
    'Open Delete Confirm': props<{
      teammate: fromGenerated.AdminTeammateView;
    }>(),
    'Close Delete Confirm': emptyProps(),
    'Close Modal': emptyProps(),

    // ─── Staged-data mutations (Builder edits) ───────────────────────────────
    'Set Visual Part': props<{ part: VisualPart; value: string }>(),
    'Set Translation Field': props<{
      field: TeammateTranslationFieldKey;
      locale: TeammateBuilderLocale;
      value: string;
    }>(),
    'Set Access Mode': props<{ mode: AdminTeammatesAccessMode }>(),
    'Add Invitee': props<{ invitee: AdminTeammateInvitee }>(),
    'Remove Invitee': props<{ id: string; kind: 'user' | 'group' }>(),
    'Add Attached Skill': props<{ skillId: string }>(),
    'Remove Attached Skill': props<{ skillId: string }>(),

    // ─── Skills typeahead ────────────────────────────────────────────────────
    'Search Skills': props<{ query: string }>(),
    'Search Skills Success': props<{
      results: fromGenerated.AdminSkillView[];
    }>(),
    'Search Skills Failure': props<{ error: string }>(),

    // ─── Create / Update / Delete ────────────────────────────────────────────
    'Create Teammate': props<{
      body: CreateTeammateBody;
      accessMode: AdminTeammatesAccessMode;
      invitees: AdminTeammateInvitee[];
      allGroupId: string | null;
    }>(),
    'Create Teammate Success': props<{
      teammate: fromGenerated.AdminTeammateView;
      accessMode: AdminTeammatesAccessMode;
      invitees: AdminTeammateInvitee[];
      allGroupId: string | null;
    }>(),
    'Create Teammate Failure': props<{ error: string }>(),

    'Update Teammate Content': props<{
      teammateId: string;
      body: fromGenerated.UpdateTeammateDto;
    }>(),
    'Update Teammate Content Success': props<{
      teammate: fromGenerated.AdminTeammateView;
    }>(),
    'Update Teammate Content Failure': props<{
      teammateId: string;
      error: string;
    }>(),

    'Delete Teammate': props<{
      teammateId: string;
      teammate: fromGenerated.AdminTeammateView;
    }>(),
    'Delete Teammate Success': props<{
      teammateId: string;
      teammate: fromGenerated.AdminTeammateView;
    }>(),
    'Delete Teammate Failure': props<{ teammateId: string; error: string }>(),

    // ─── Invitee fan-out (one leaf per add/remove) ───────────────────────────
    // `adds`/`removes` are the whole story: the dispatcher resolves access mode
    // into them before dispatching, so the all-group arrives here like any other
    // invitee. Carrying the org's all-group id as well invited the effect to
    // grant it on truthiness alone (audit P0 #2).
    'Fan Out Invitee Diff': props<{
      batchId: string; // Correlation ID for aggregation (KD-5: concurrency-safe keying)
      teammateId: string;
      teammate: fromGenerated.AdminTeammateView;
      // The share set as it stood before this save — the same set `adds` and
      // `removes` were diffed from, so `teammate_shared`'s final-state count
      // cannot disagree with the diff that produced it. Not derived from
      // `AdminTeammateView.userCount`/`groupCount`: those are an independently
      // sourced snapshot that can drift from the Builder's own before-set.
      initialInvitees: AdminTeammateInvitee[];
      adds: AdminTeammateInvitee[];
      removes: AdminTeammateInvitee[];
    }>(),

    'Grant User Teammate Access': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      userId: string;
      userDisplayName: string;
    }>(),
    'Grant User Teammate Access Success': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      userId: string;
    }>(),
    'Grant User Teammate Access Failure': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      userId: string;
      userDisplayName: string;
      error: string;
    }>(),

    'Revoke User Teammate Access': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      userId: string;
      userDisplayName: string;
    }>(),
    'Revoke User Teammate Access Success': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      userId: string;
    }>(),
    'Revoke User Teammate Access Failure': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      userId: string;
      userDisplayName: string;
      error: string;
    }>(),

    'Grant Group Teammate Access': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
    }>(),
    'Grant Group Teammate Access Success': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      groupId: string;
    }>(),
    'Grant Group Teammate Access Failure': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
      error: string;
    }>(),

    'Revoke Group Teammate Access': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
    }>(),
    'Revoke Group Teammate Access Success': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      groupId: string;
    }>(),
    'Revoke Group Teammate Access Failure': props<{
      batchId: string; // Correlation ID for aggregation
      teammateId: string;
      groupId: string;
      groupDisplayName: string;
      error: string;
    }>(),
  },
});
